import React, { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ContactActions } from "@/components/ContactActions";
import { Icon } from "@/components/kit";
import { images, categories } from "@/theme";
import { proLink } from "@/api";
import { useI18n } from "@/i18n";

export default function Home() {
  const navigate = useNavigate();
  const { t, lang } = useI18n();

  // Accès direct via quotedesign.app (sans lien pro dédié) -> aucune attribution partenaire :
  // on efface tout slug pro persisté d'une visite précédente. Un lien pro dédié (/p/:slug)
  // marque une redirection en mémoire juste avant d'arriver ici, ce qui conserve son slug.
  useEffect(() => {
    if (!proLink.wasRedirect()) proLink.clear();
  }, []);

  return (
    <div className="min-h-screen bg-paper">
      {/* Hero */}
      <section className="relative flex min-h-[440px] flex-col justify-end overflow-hidden sm:min-h-[520px]">
        <img src={images.hero} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#2a2624]/10 via-[#2a2624]/35 to-[#2a2624]/90" />

        <div className="absolute left-0 right-0 top-0 z-10">
          <div className="mx-auto flex max-w-6xl items-center justify-between px-5 pt-5">
            <div className="flex items-center gap-3">
              <span className="text-xs font-semibold tracking-[0.2em] text-brand-secondary">
                DÉCO LIGOT · BRUXELLES
              </span>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/admin"
                data-testid="hero-admin-link"
                className="flex items-center gap-1.5 rounded-full bg-white/15 px-3.5 py-1.5 text-[13px] font-medium text-white backdrop-blur transition-colors hover:bg-white/25"
              >
                <Icon name="lock" size={14} /> {t("tab_pro")}
              </Link>
              <LanguageSwitcher dark />
            </div>
          </div>
        </div>

        <div className="relative z-10 mx-auto w-full max-w-6xl px-5 pb-12">
          <h1 className="whitespace-pre-line font-display text-5xl font-bold leading-[1.02] text-white sm:text-6xl lg:text-7xl">
            {t("hero_title")}
          </h1>
          <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-white/85">{t("hero_sub")}</p>
        </div>
      </section>

      {/* Body */}
      <main className="mx-auto max-w-6xl px-5 py-12">
        <div className="flex flex-wrap items-center gap-3">
          <img
            src="/logo_quotedesign.png"
            alt="QuoteDesign.app"
            data-testid="services-logo"
            className="h-12 w-auto shrink-0 rounded-md bg-white p-1 sm:h-14"
          />
          <h2 className="font-display text-3xl font-semibold text-ink sm:text-4xl">{t("services_title")}</h2>
          {lang === "fr" && (
            <a
              href="https://youtube.com/shorts/yqCnAruh31M?si=8zWBHzmMYRTsyj1P"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="video-button"
              className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-brand-primary/30 bg-white px-4 py-2 text-sm font-medium text-brand-primary shadow-sm transition-colors hover:bg-brand-primary hover:text-white"
            >
              <Icon name="play" size={15} /> Vidéo
            </a>
          )}
        </div>
        <p className="mb-8 mt-1 text-[15px] text-mute">{t("services_sub")}</p>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {categories.map((c, i) => (
            <button
              key={c.key}
              type="button"
              data-testid={`category-${c.key}`}
              onClick={() => navigate(`/quote/${c.key}`)}
              style={{ animationDelay: `${i * 70}ms` }}
              className="group relative flex h-56 animate-fade-up flex-col justify-end overflow-hidden rounded-2xl text-left"
            >
              <img
                src={c.image}
                alt=""
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2a2624]/90 to-transparent" />
              <span className="absolute left-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 backdrop-blur">
                <Icon name={c.icon} size={18} className="text-ink" />
              </span>
              <div className="relative p-4">
                <p className="text-[16px] font-semibold text-white">{t(`cat_${c.key}`)}</p>
                <p className="mt-0.5 text-xs text-white/80">{t(`cat_${c.key}_short`)}</p>
              </div>
            </button>
          ))}
        </div>

        <div className="mt-8 flex items-start gap-3 rounded-2xl bg-brand-tertiary p-5">
          <Icon name="zap" size={22} className="mt-0.5 shrink-0 text-brand" />
          <div>
            <p className="text-[15px] font-semibold text-brand-on">{t("ai_info_title")}</p>
            <p className="mt-0.5 text-[13px] leading-relaxed text-ink-3">{t("ai_info_text")}</p>
          </div>
        </div>

        <div className="mt-10">
          <ContactActions showLogo />
        </div>
      </main>
      <footer className="border-t border-black/10 px-5 py-8 text-center" data-testid="home-footer">
        <img
          src="/logo_quotedesign.png"
          alt="QuoteDesign.app"
          data-testid="footer-logo"
          className="mx-auto mb-5 h-16 w-auto rounded-md bg-white p-1"
        />
        <p className="mx-auto max-w-2xl text-[12px] leading-relaxed text-mute">
          QuoteDesign est un outil fourni par Déco Ligot. Les devis émis par des professionnels indépendants n&apos;engagent pas la société Déco Ligot.
        </p>
        <div className="mt-3 flex items-center justify-center gap-4">
          <a data-testid="footer-privacy" href="https://decoligot.be/politique-de-confidentialite/" target="_blank" rel="noopener noreferrer" className="text-[12px] font-medium text-brand underline underline-offset-2 hover:text-brand-on">
            Confidentialité
          </a>
          <a data-testid="footer-legal" href="https://decoligot.be/mentions-legales/" target="_blank" rel="noopener noreferrer" className="text-[12px] font-medium text-brand underline underline-offset-2 hover:text-brand-on">
            Mentions légales
          </a>
        </div>
      </footer>
    </div>
  );
}
